import React, { useReducer, useContext, useMemo } from 'react';

import {
  type CheckoutState,
  type CheckoutAction,
  checkoutReducer,
  defaultState,
} from './reducer';
type CheckoutProvider = React.FC<
  React.PropsWithChildren<{ initialState?: CheckoutState }>
>;
interface CheckoutContext {
  Provider: CheckoutProvider;
  useStore: () => CheckoutContextValue;
}
interface CheckoutContextValue {
  state: CheckoutState;
  dispatch: React.Dispatch<CheckoutAction>;
}

const createCheckoutContext = (
  reducer: React.Reducer<CheckoutState, CheckoutAction>,
): CheckoutContext => {
  const Context = React.createContext({} as CheckoutContextValue);

  const Provider: CheckoutProvider = ({
    initialState = defaultState,
    children,
  }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const providerValue = useMemo(
      () => ({
        state,
        dispatch,
      }),
      [state, dispatch],
    );

    return (
      <Context.Provider value={providerValue}>{children}</Context.Provider>
    );
  };

  const useStore = (): CheckoutContextValue => {
    const context = useContext(Context);
    if (!context) {
      throw new Error('Checkout store must be used with Checkout Provider!');
    }
    return context;
  };

  return {
    Provider,
    useStore,
  };
};

const { Provider, useStore } = createCheckoutContext(checkoutReducer);

export { Provider, useStore };
